import type { FormEvent } from "react";
import { heroEntries, heroStats } from "@/mocks/navData";
import { toneIconClass } from "./tones";

interface HeroProps {
  query: string;
  onQueryChange: (value: string) => void;
}

export default function Hero({ query, onQueryChange }: HeroProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = document.getElementById("sites");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="top" className="scroll-mt-20">
      <div className="w-full px-4 md:px-6 pt-12 md:pt-20 pb-10 md:pb-16">
        <div className="mx-auto max-w-shell">
          <div className="max-w-3xl animate-rise">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-50 border border-accent-200 text-xs md:text-sm text-accent-800">
              <i className="ri-compass-3-line"></i>
              51导航大全 · 已整理 1,286 个可用站点
            </span>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-semibold leading-tight text-foreground-950">
              不知道上什么网？
              <br className="hidden sm:block" />
              按分类挑，一眼锁定靠谱站点
            </h1>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground-600">
              51网址导航大全按分类整理收录热门网站列表，涵盖常用工具、影视娱乐、学习资源等方向。想找靠谱好站又没头绪时，直接看分类推荐就能快速锁定目标，省时又省心。
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-8 md:mt-10 max-w-2xl animate-rise delay-1"
          >
            <div className="flex flex-col sm:flex-row gap-3 p-2 rounded-lg bg-background-50 border border-background-300">
              <label htmlFor="hero-search" className="flex-1 flex items-center gap-3 px-3">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  id="hero-search"
                  type="text"
                  value={query}
                  onChange={(event) => onQueryChange(event.target.value)}
                  placeholder="输入关键词，如「公开课」「截图工具」「电台」"
                  className="w-full bg-transparent py-3 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </label>
              <button
                type="submit"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-3-line"></i>
                在收录列表里找
              </button>
            </div>
            <p className="mt-3 text-xs text-foreground-500">
              常用入口：公开课 · 截图工具 · 比价 · 电台 · 速查手册
            </p>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 pb-12 md:pb-16">
        <div className="mx-auto max-w-shell">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {heroEntries.map((entry, index) => (
              <a
                key={entry.name}
                href="#categories"
                className={`group flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer animate-rise delay-${(index % 6) + 1}`}
              >
                <span
                  className={`shrink-0 w-12 h-12 rounded-lg border flex items-center justify-center ${toneIconClass[entry.tone]}`}
                >
                  <i className={`${entry.icon} text-xl`}></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-heading text-base font-semibold text-foreground-950">
                      {entry.name}
                    </span>
                    <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                  </span>
                  <span className="mt-1 block text-sm text-foreground-600 leading-relaxed">
                    {entry.desc}
                  </span>
                  <span className="mt-2 inline-block text-xs text-foreground-500">
                    {entry.count}
                  </span>
                </span>
              </a>
            ))}
          </div>

          <dl className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200 animate-rise delay-3">
            {heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <dt className="text-xs md:text-sm text-foreground-500">{stat.label}</dt>
                <dd className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}